import { useCallback, useEffect, useState } from "react";
import { useQuery } from "react-query";

import { DealersService } from "~/entity/Dealers/api/service";

const PAGE_SIZE = 100;

// GET /dealer/dealers с пагинацией и поиском. Опциональный regmenId — фильтр по
// региональному менеджеру (когда в строке выбран РМ). Без regmenId — все дилеры.
export function useDealersForAssignment(regmenId?: number) {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [items, setItems] = useState<
    Awaited<
      ReturnType<typeof DealersService.GET.getDealerList>
    >["data"]["dealers"]
  >([]);

  // При смене regmenId (пользователь выбрал другого РМ) сбрасываем накопленное
  // состояние, иначе в списке смешаются дилеры разных РМ.
  useEffect(() => {
    setSearch("");
    setPage(0);
    setItems([]);
  }, [regmenId]);

  const { data, isLoading, isFetching } = useQuery(
    ["dealerList", regmenId, search, page],
    () =>
      DealersService.GET.getDealerList({
        regmenId,
        dealerSearch: search ? { dealerName: search } : undefined,
        // Только активные дилеры (PASDILER-2066).
        status: "active",
        page,
        count: PAGE_SIZE,
      }).then((res) => res.data),
    { keepPreviousData: true },
  );

  const totalPages = data?.totalPages ?? 1;

  // Накапливаем dealers по страницам: page 0 — заменяем (новый поиск/фильтр),
  // page > 0 — дополняем без дублей по id.
  useEffect(() => {
    const pageItems = data?.dealers ?? [];
    if (pageItems.length === 0) return;
    if (page === 0) {
      setItems(pageItems);
    } else {
      setItems((prev) => {
        const seen = new Set(prev.map((m) => m.id));
        return [...prev, ...pageItems.filter((m) => !seen.has(m.id))];
      });
    }
  }, [data, page]);

  const onSearch = useCallback((value: string) => {
    setSearch(value);
    setPage(0);
  }, []);

  const onPopupScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const el = e.currentTarget;
      if (
        el.scrollTop + el.clientHeight >= el.scrollHeight - 20 &&
        !isLoading &&
        // page 0-indexed: следующая страница существует, пока page + 1 < totalPages
        page + 1 < totalPages
      ) {
        setPage((p) => p + 1);
      }
    },
    [isLoading, page, totalPages],
  );

  return { items, isLoading, isFetching, onSearch, onPopupScroll };
}
